<template>
  <div ref="videoWrap" class="video-wrap">
    <div id="interactiveVideo"></div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Loading from '@/components/resources-loading';
import { Content } from '@forchange/storytime';
import { namespace } from 'vuex-class';
import Player, { IPlayerOptions } from 'xgplayer';
import './styles/.xgplayer/skin/index';
import { HdvVideo } from '@forchange/cleword-utils';

interface IVideoTrackData {
  startPosition: number; // 开始位置
  playDuration: number; // 实际播放时长
  pausesPosition: string[]; // 主动暂停的位置
  waitTimes: number; // 缓冲的次数
}

const commonModal = namespace('common');

@Component({
  name: 'Video',
  components: {
    Loading
  }
})
export default class Video extends Vue {
  @Prop({ type: Object, default: () => {} }) content!: Content & {
    url: string;
    poster?: string;
  };
  @Prop({}) isCurrentContent!: boolean;
  @Prop() isInLookBack!: boolean;

  loading = true;
  player:
    | (Player & { poster?: string; defaultPlaybackRate?: number; video?: HTMLVideoElement })
    | undefined;
  definitionList: { name: string; url: string }[] = [];
  played = false; // 是否有播放过，用于判断是否使用player.start来播放

  @commonModal.State('courseName') courseName: string;

  @Watch('content')
  handleVideoChange() {
    if (!this.player) return;
    // reset
    this.resetTrackData();
    this.clearWaitTimer();
    this.clearCountTimeTimer();
    const videoInfo = new HdvVideo(this.content.url);
    const url = videoInfo.hdv720 || this.content.url;
    const poster = this.content.poster || videoInfo.cover || '';

    this.player.poster = poster;
    this.player.emit('resetTime');
    if (this.played) {
      this.player.src = url;
    } else {
      this.player.start(url);
    }
    this.initDefinition(videoInfo);
    // 防止从call视频回来后背景视频自动播放的问题
    if (this.isCurrentContent) {
      this.player.play();
    }
  }

  initVideo() {
    let ignores: any = ['fullscreen', 'cssFullscreen', 'replay'];
    if (!this.isInLookBack) {
      ignores = [...ignores, 'skip'];
    }
    const videoInfo = new HdvVideo(this.content.url);
    const url = videoInfo.hdv720 || this.content.url;
    const poster = this.content.poster || videoInfo.cover || '';

    this.player = new Player({
      id: 'interactiveVideo',
      width: '100%',
      height: '100%',
      url,
      poster,
      playbackRate: [0.5, 1, 1.25, 1.5, 2],
      volume: 1,
      playsinline: true,
      enableVideoDbltouch: true,
      ignores
    });
    this.initDefinition(videoInfo);
  }

  initDefinition(info: HdvVideo) {
    if (!this.player) return;
    const defaultList = [{ name: '原画', url: this.content.url }];
    if (info.hdv720) {
      defaultList.push({
        name: '高清',
        url: info.hdv720
      });
    }

    if (info.hdv480) {
      defaultList.push({
        name: '流畅',
        url: info.hdv480
      });
    }

    this.player.emit('resourceReady', defaultList);
  }

  handleEnd() {
    this.$emit('changeTitleVisible');

    // 埋点相关逻辑
    this.trackVideoData();

    // 防止因拖动滚动条触发多次end事件导致多次加载下一条资源的问题
    if (!this.isCurrentContent) return;
    this.$emit('next');
  }
  handleBlur() {
    if (this.player?.paused) return;
    this.$emit('changeTitleVisible');
  }
  handleFocus() {
    this.$emit('changeTitleVisible', true);
  }
  handlePause() {
    this.$emit('changeTitleVisible', true);
    // 埋点相关逻辑
    if (!this.player) return;
    this.stopCountTime();
    if (!this.player.ended && !this.isDefinitionChange) {
      const pauseCurTime = String(Math.ceil(this.player.currentTime));
      this.trackData.pausesPosition.push(pauseCurTime);
    }
  }

  handlePlay() {
    if (!this.played) {
      this.played = true;
    }

    // 埋点相关逻辑
    this.startCountTime();
    this.curVideoPlayed = true;
    this.isDefinitionChange = false;
    if (this.trackData.startPosition < 0) {
      this.trackData.startPosition = this.player?.currentTime || 0;
    }
  }

  handleVideoError(e: any) {
    this.$emit('changeTitleVisible', true);
  }

  handleSkipResource() {
    // 兼容当前资源不是视频但是重播视频出错跳过的情况
    if (this.isCurrentContent) {
      this.$emit('next');
    }
  }

  handleSkip() {
    this.player?.pause();
    this.$emit('next');
  }

  mounted() {
    this.initVideo();
    this.player?.on('ended', this.handleEnd);
    this.player?.on('error', this.handleVideoError);
    this.player?.on('load-next-resources', this.handleSkipResource);
    this.player?.on('play', this.handlePlay);
    this.player?.on('focus', this.handleFocus);
    this.player?.on('blur', this.handleBlur);
    this.player?.on('pause', this.handlePause);
    this.player?.on('waiting', this.handleWaiting);
    this.player?.on('playing', this.handlePlaying);
    this.player?.on('definitionClick', this.onDefinitionClick);
    this.player?.on('video_skip', this.handleSkip);

    this.loading = false;
  }

  beforeDestroy() {
    this.trackVideoData();
    this.resetTrackData();
    this.clearWaitTimer();
    this.clearCountTimeTimer();
    this.player = undefined;
  }

  // 埋点使用数据
  curVideoPlayed = false; // 用于记录当前视频是否有播放过 若没播放则不做记录
  isDefinitionChange = false; // 用于标记是否是切换清晰度引起的暂停，若是则不计入暂停次数
  countTimeTimer: number | null = null; // 用于统计实际观看时间的计时器
  waitTimer: number | null = null; // 用于计算是否在缓冲

  trackData: IVideoTrackData = {
    startPosition: -1, // 开始位置
    playDuration: 0, // 实际播放时长
    pausesPosition: [], // 主动暂停的位置
    waitTimes: 0 // 缓冲的次数
  };
  // 埋点相关
  startCountTime() {
    this.countTimeTimer = window.setInterval(() => {
      this.trackData.playDuration += 0.5;
    }, 500);
  }
  stopCountTime() {
    this.countTimeTimer && clearInterval(this.countTimeTimer);
    this.countTimeTimer = null;
  }
  onDefinitionClick() {
    this.isDefinitionChange = true;
  }
  handlePlaying() {
    this.clearWaitTimer();
  }
  handleWaiting() {
    this.clearWaitTimer();
    this.waitTimer = window.setTimeout(() => {
      this.trackData.waitTimes++;
    }, 1000);
  }
  clearWaitTimer() {
    if (!this.waitTimer) return;
    clearTimeout(this.waitTimer);
    this.waitTimer = null;
  }
  clearCountTimeTimer() {
    if (!this.countTimeTimer) return;
    clearTimeout(this.countTimeTimer);
    this.countTimeTimer = null;
  }

  resetTrackData() {
    this.trackData = {
      startPosition: -1,
      playDuration: 0,
      pausesPosition: [],
      waitTimes: 0
    };
    this.curVideoPlayed = false;
    this.isDefinitionChange = false;
  }

  trackVideoData() {
    if (this.isInLookBack || !this.player || !this.curVideoPlayed) return;

    window.$tracker?.tracker?.videoScenePlay({
      resource_id: this.content.$id,
      video_length: Math.ceil(this.player.duration || 0),
      play_duration: Math.ceil(this.trackData.playDuration),
      start_position: String(Math.round(this.trackData.startPosition)),
      end_position: String(Math.ceil(this.player.currentTime)),
      pauses_times: this.trackData.pausesPosition.length,
      pauses_position: this.trackData.pausesPosition,
      wait_times: this.trackData.waitTimes,
      has_finished: this.player.ended,
      play_speed: this.player.playbackRate
    });
  }

  onBeforeUnload() {
    if (this.isInLookBack || !this.player) return;
    if (!this.player.ended) {
      this.trackVideoData();
    }
    this.resetTrackData();
    this.clearWaitTimer();
    this.clearCountTimeTimer();
    this.player = undefined;
  }
}
</script>
<style scoped>
.player {
  @apply flex items-center justify-center w-full rounded-motif overflow-hidden;
}

.video-wrap {
  --plyr-color-main: rgba(0, 0, 0, 0.3);
  --plyr-badge-background: var(--motif-400);
  --plyr-range-fill-background: var(--motif-400);
  --plyr-control-toggle-checked-background: var(--motif-400);
  @apply absolute left-0 top-0 w-full h-full flex items-center  bg-black;
  z-index: 1;
}
</style>
